import {Notification} from '../../modules/notification.js';
import {Spinner} from '../../modules/spinner.js';
import {Button} from '../../modules/button.js';
import {getHttpClient} from '../../modules/httpClient.js';
import {dangerouslyConvertHtmlStringToElement} from '../../utils/markupHelpers.js';
import {getWindowWidth, getWindowHeight} from '../../modules/breakpoint.js';
import {t} from '../../localization/translate.js';
import {initClonedPopovers} from '../../components/popover/popover.js';

/**
 * Discussion Messages Manager
 * Handles all conversation interactions including messages, replies, likes, and editing
 */
class DiscussionManager {
    constructor() {
        this.httpClient = getHttpClient();
        this.constants = {
            SELECTORS: {
                // Main containers
                conversationList: '.js-conversation-list',
                messageContainer: '.js-new-message-container',
                messageRoot: '.js-target-message-root',
                message: '.js-target-message',

                // Forms and inputs
                newMessageForm: '.js-new-message-form',
                addMessageBlock: '.js-add-message-block',
                topicAddMessage: '.js-topic-add-message',
                replyToInput: '.js-reply_to_message_id-input',

                // Reply system
                repliesWrapper: '.js-target-replies-wrapper',
                repliesContainer: '.js-target-replies',
                messageRepliesWrapper: '.js-message-replies-wrapper',
                replyMessageWrapper: '.js-reply-message-wrapper',

                // Actions
                likeButton: '.js-like-btn',
                replyButton: '.js-reply',
                editButton: '.js-editDiscussionMessage',
                deleteButton: '.js-delete-message',
                saveButton: '.js-save-message',
                cancelButton: '.js-close-message-editor',
                cancelReplyButton: '.js-cancel-reply',
                replySubmitButton: '.rte-target-submit',

                // UI elements
                likeCount: '.js-target-like-count',
                messageText: '.js-target-text',
                editControls: '.js-target-edit-message-controls',
                editTemplate: '.js-edit-message-template',
                orderSelect: '[data-discussion-topic-order]',
            },

            CSS_CLASSES: {
                liked: 'js-liked',
                focused: 'addMessageBlock--focused',
                hidden: 'hidden',
                opacity0: 'opacity-0',
                slideDown: 'animate-slide-down',
                // button.js gates a `pulseInView` button's rings with this hook until the button scrolls into view.
                pulseInViewGate: 'js-buttonPulseInView',
                textRed: 'fg-text-brand-default',
                textNeutral: 'fg-text-neutral-default',
            },

            REPLY_WRAPPER_CLASSES: `js-reply-message-wrapper max-w-306 md:max-w-494 lg:max-w-673 xl:max-w-672 w-full first:mt-0`,
        };

        this.initialize();
    }

    /**
     * Initialize the discussion manager
     */
    initialize() {
        this.initializeRTE();
        this.bindEventListeners();
        this.scrollToLatestMessageIfReversed();
    }

    // ==========================================
    // INITIALIZATION METHODS
    // ==========================================

    /**
     * Initialize Rich Text Editor with custom settings
     */
    initializeRTE() {
        document.querySelectorAll('rt-editor').forEach((editor) => {
            const submitButtons = editor.closest('form').querySelectorAll('button[type="submit"]');

            if (submitButtons.length) {
                // Handle editor content changes
                editor.addEventListener('rte:change', () => {
                    const isTextareaEmpty = editor.value === '';
                    submitButtons[0].disabled = isTextareaEmpty;

                    if (!isTextareaEmpty) {
                        editor.closest('.group')?.classList.remove('error');
                    }
                });

                // Handle editor focus
                editor.addEventListener('rte:focus', () => {
                    const messageBlock = editor.closest(this.constants.SELECTORS.addMessageBlock);
                    if (messageBlock) {
                        messageBlock.classList.add(this.constants.CSS_CLASSES.focused);
                    }
                });
            }

            // Handle lesson page specific functionality
            this.handleLessonPageRTE();
        });
    }

    /**
     * Handle lesson page specific RTE functionality
     */
    handleLessonPageRTE() {
        const lessonPager = document.querySelector('.lessonPager');
        const discussionContainer = document.querySelector('.lessonItemSpecial .discussionContainer');

        // Only add 'inside-discussion' class if both elements are present
        if (lessonPager && discussionContainer) {
            lessonPager.classList.add('inside-discussion');
            discussionContainer.append(lessonPager);
        }
    }

    /**
     * Scroll to latest message on page load if reverse display order is active
     */
    scrollToLatestMessageIfReversed() {
        const messageContainer = document.querySelector(this.constants.SELECTORS.messageContainer);
        if (!messageContainer) return;

        const reverseDisplayOrder = messageContainer.dataset.reverseDisplayOrder === 'true';
        if (reverseDisplayOrder) {
            const latestMessage = document.getElementById('js-latest-message');
            if (latestMessage) {
                // Use setTimeout to ensure DOM is fully rendered
                setTimeout(() => {
                    latestMessage.scrollIntoView({behavior: 'smooth', block: 'end'});
                }, 0);
            }
        }
    }

    /**
     * Bind all event listeners
     */
    bindEventListeners() {
        this.bindFormSubmission();
        this.bindLikeActions();
        this.bindReplyActions();
        this.bindEditActions();
        this.bindDeleteActions();
        this.bindOrderChange();
    }

    // ==========================================
    // EVENT BINDING METHODS
    // ==========================================

    /**
     * Bind form submission events
     */
    bindFormSubmission() {
        document.addEventListener('submit', (event) => {
            const form = event.target.closest(this.constants.SELECTORS.newMessageForm);
            if (form instanceof HTMLFormElement) {
                event.preventDefault();
                this.handleNewMessageSubmission(form);
            }
        });
    }

    /**
     * Bind like/unlike button events
     */
    bindLikeActions() {
        document.addEventListener('click', (event) => {
            const likeButton = event.target.closest(this.constants.SELECTORS.likeButton);
            if (likeButton) {
                event.preventDefault();
                this.handleLikeToggle(likeButton);
            }
        });
    }

    /**
     * Bind reply button events
     */
    bindReplyActions() {
        document.addEventListener('click', (event) => {
            const replyButton = event.target.closest(this.constants.SELECTORS.replyButton);
            const cancelReplyButton = event.target.closest(this.constants.SELECTORS.cancelReplyButton);

            if (replyButton) {
                event.preventDefault();
                this.handleReplyClick(replyButton);
            } else if (cancelReplyButton) {
                event.preventDefault();
                this.handleCancelReply(cancelReplyButton);
            }
        });
    }

    /**
     * Bind edit message events
     */
    bindEditActions() {
        document.addEventListener('click', (event) => {
            const editButton = event.target.closest(this.constants.SELECTORS.editButton);
            const saveButton = event.target.closest(this.constants.SELECTORS.saveButton);
            const cancelButton = event.target.closest(this.constants.SELECTORS.cancelButton);

            if (editButton) {
                event.preventDefault();
                this.handleEditMessage(editButton);
            } else if (saveButton) {
                this.handleSaveMessage(saveButton);
            } else if (cancelButton) {
                this.handleCancelEdit(cancelButton);
            }
        });
    }

    /**
     * Bind delete message events
     */
    bindDeleteActions() {
        document.querySelectorAll('#deleteMessageDialog,#deleteReplyDialog').forEach((dialogElement) => {
            let originalMessage;

            dialogElement.addEventListener('open', (event) => {
                const activationElement = event.detail;
                if (!activationElement) {
                    event.preventDefault();
                } else {
                    originalMessage = this.getMessageByChild(activationElement);
                }
            });

            dialogElement.addEventListener('close', async () => {
                if (dialogElement.returnValue === 'DELETE_ACTION') {
                    await this.handleDeleteMessage(originalMessage);
                }
            });
        });
    }

    /**
     * Bind order change events
     */
    bindOrderChange() {
        const selectInput = document.querySelector(this.constants.SELECTORS.orderSelect);
        if (selectInput) {
            selectInput.addEventListener('change', ({target}) => {
                let parameters = new URLSearchParams(document.location.search.substring(1));
                parameters.set('orderBy', target.value);
                window.location.search = parameters.toString();
            });
        }
    }

    // ==========================================
    // MESSAGE SUBMISSION METHODS
    // ==========================================

    /**
     * Handle new message form submission
     * @param {HTMLFormElement} form - The form being submitted
     */
    async handleNewMessageSubmission(form) {
        const submitButton = form.querySelector('[type="submit"]');
        // Prevent duplicate submissions on fast clicks
        submitButton.disabled = true;
        Button.addLoading(submitButton, t('Posting...'));

        const formData = new FormData(form);

        try {
            const response = await this.httpClient.post(form.action, formData);

            if (response) {
                await this.processNewMessageResponse(response, form);
            }
        } catch (error) {
            this.handleSubmissionError(error, form);
        } finally {
            submitButton.disabled = false;
            Button.removeLoading(submitButton);
        }
    }

    /**
     * Process successful new message response
     * @param {object} response - Server response
     * @param {HTMLFormElement} form - The form that was submitted
     */
    async processNewMessageResponse(response, form) {
        const newMessage = dangerouslyConvertHtmlStringToElement(response.html);
        const isReply = response.model && response.model.reply_to_id;
        const editor = form.querySelector('rt-editor');

        if (isReply) {
            this.handleReplySubmission(newMessage, form, response.model);
        } else {
            this.handleMainMessageSubmission(newMessage);
        }

        // Update URL and scroll to new message
        history.pushState(null, '', `#m${response.model.id}`);
        editor.value = '';
        this.scrollViewportToElement(newMessage);

        // Remove any existing error messages
        form.querySelector('.alertContainer')?.remove();

        document.querySelectorAll('.js-no-messages-container')
              .forEach((stubMessage) => stubMessage.classList.add('hidden'));
    }

    /**
     * Handle reply message submission
     * @param {HTMLElement} newMessage - The new message element
     * @param {HTMLFormElement} form - The form that was submitted
     */
    handleReplySubmission(newMessage, form) {
        // Find the parent root message
        const parentRootMessage = this.getRootMessageByChild(form);

        if (parentRootMessage) {
            // Hide the replies wrapper (used for reply form)
            const repliesWrapper = parentRootMessage.querySelector(this.constants.SELECTORS.repliesWrapper);
            if (repliesWrapper) {
                repliesWrapper.hidden = true;
            }

            // Create and add reply wrapper with proper styling to the main replies container
            const replyWrapper = this.createReplyWrapper(newMessage);
            const messageRepliesWrapper = parentRootMessage.querySelector(this.constants.SELECTORS.messageRepliesWrapper);

            if (messageRepliesWrapper) {
                messageRepliesWrapper.hidden = false;
                messageRepliesWrapper.append(replyWrapper);
            }
        }

        // Remove the cloned reply form (it's inside a temporary wrapper)
        const temporaryWrapper = form.closest('.js-temp-reply-wrapper');
        if (temporaryWrapper) {
            temporaryWrapper.remove();
        }
    }

    /**
     * Handle main message submission (not a reply)
     * @param {HTMLElement} newMessage - The new message element
     */
    handleMainMessageSubmission(newMessage) {
        const newMessageContainer = document.querySelector(this.constants.SELECTORS.messageContainer);

        // Check if container exists before proceeding
        if (!newMessageContainer) {
            return;
        }

        // Show the replies section if it was hidden (first message scenario)
        const repliesSection = newMessageContainer.closest('[data-replies-section]');
        if (repliesSection) {
            repliesSection.classList.remove('hidden');
        }

        const orderBy = newMessageContainer.dataset.orderBy;
        const reverseDisplayOrder = newMessageContainer.dataset.reverseDisplayOrder === 'true';

        // Wrap the new message in a list item since the container is an <ol>
        const listItem = document.createElement('li');
        listItem.append(newMessage);

        // If reverse display order is active, always append to bottom
        // Otherwise use the original logic based on orderBy
        if (reverseDisplayOrder || ['popular', 'asc'].includes(orderBy)) {
            newMessageContainer.append(listItem);
        } else {
            newMessageContainer.prepend(listItem);
        }
    }

    /**
     * Handle form submission errors
     * @param {Error} error - The error object
     * @param {HTMLFormElement} form - The form that failed
     */
    handleSubmissionError(error, form) {
        let errorMessage = error.message;

        if (error.status === 422 && error.errorBody?.errors?.message_text) {
            errorMessage = error.errorBody.errors.message_text.join('\n');
        }

        Notification.error(errorMessage, form);
    }

    // ==========================================
    // LIKE/UNLIKE METHODS
    // ==========================================

    /**
     * Handle like button toggle
     * @param {HTMLElement} likeButton - The like button that was clicked
     */
    async handleLikeToggle(likeButton) {
        const message = likeButton.closest(this.constants.SELECTORS.message);
        const isLiking = !message.classList.contains(this.constants.CSS_CLASSES.liked);

        this.setLikeButtonState(likeButton, true);
        this.updateLikeUI(message, isLiking ? 1 : -1);

        const messageId = message.dataset.messageId;
        const endpoint = `/conversation/messages/${messageId}/like`;

        try {
            if (isLiking) {
                await this.httpClient.post(endpoint);
            } else {
                await this.httpClient.delete(endpoint);
            }
            likeButton.classList.add('animate-liked');
        } catch (error) {
            // Rollback UI changes on error
            if (error.status === 409) {
                this.updateLikeUI(message, isLiking ? -1 : 1);
            }
            Notification.error(error.message);
        } finally {
            this.setLikeButtonState(likeButton, false);
        }
    }

    /**
     * Update like UI elements
     * @param {HTMLElement} message - The message element
     * @param {number} likeDifference - The change in like count (+1 or -1)
     */
    updateLikeUI(message, likeDifference) {
        const likeCount = message.querySelector(this.constants.SELECTORS.likeCount);
        const likeButtonSvg = message.querySelector(`${this.constants.SELECTORS.likeButton} > svg > use`);
        const likeButton = message.querySelector(this.constants.SELECTORS.likeButton);

        // Update like count
        const currentNumberOfLikes = parseInt(likeCount.textContent) + likeDifference;
        likeCount.textContent = `${currentNumberOfLikes}`;

        // Update heart icon
        const spriteUrl = likeButtonSvg.getAttribute('href').split('#')[0];
        const svgUrl = spriteUrl + '#icon-' + (likeDifference > 0 ? 'heart-filled' : 'heart');
        likeButtonSvg.setAttribute('href', svgUrl);

        // Update message and button classes
        message.classList.toggle(this.constants.CSS_CLASSES.liked);

        if (likeDifference > 0) {
            likeButton.classList.add(this.constants.CSS_CLASSES.textRed);
        } else if (currentNumberOfLikes === 0) {
            likeButton.classList.replace(this.constants.CSS_CLASSES.textRed, this.constants.CSS_CLASSES.textNeutral);
        }
    }

    /**
     * Set like button disabled state
     * @param {HTMLElement} likeButton - The like button
     * @param {boolean} disabled - Whether to disable the button
     */
    setLikeButtonState(likeButton, disabled) {
        likeButton.disabled = disabled;
        if (!disabled) {
            likeButton.classList.remove('animate-liked');
        }
    }

    // ==========================================
    // REPLY METHODS
    // ==========================================

    /**
     * Handle reply button click
     * @param {HTMLElement} replyButton - The reply button that was clicked
     */
    handleReplyClick(replyButton) {
        const message = this.getMessageByChild(replyButton);
        if (!message || !message.parentNode) {
            return;
        }

        const replyContainer = message.parentNode.querySelector(this.constants.SELECTORS.repliesContainer);
        if (!replyContainer) {
            return;
        }

        // Hide any existing reply forms (cloned ones)
        this.hideAllReplyForms();

        // Get the original form template to clone
        const originalForm = document.querySelector(this.constants.SELECTORS.topicAddMessage);
        if (!originalForm) {
            return;
        }

        // Clone the form for the reply (keeps the original in place)
        const clonedForm = originalForm.cloneNode(true);
        clonedForm.classList.add('js-cloned-reply-form');
        initClonedPopovers(clonedForm);

        // Set up the cloned form's reply_to_message_id
        const replyInput = clonedForm.querySelector(this.constants.SELECTORS.replyToInput);
        if (!replyInput) {
            return;
        }

        replyInput.value = message.dataset.messageId;
        clonedForm.hidden = false;
        clonedForm.classList.remove('hidden');
        replyContainer.parentNode.hidden = false;

        // Replace the cloned rt-editor with a fresh one to avoid initialization issues
        // When cloned, rt-editor has internal DOM but no JavaScript state, causing double toolbars
        const clonedEditor = clonedForm.querySelector('rt-editor');
        if (clonedEditor) {
            // Create a fresh rt-editor element
            const freshEditor = document.createElement('rt-editor');

            // Copy all attributes from the original editor (from the non-cloned form)
            const originalEditor = originalForm.querySelector('rt-editor');
            if (originalEditor) {
                for (const attribute of originalEditor.attributes) {
                    freshEditor.setAttribute(attribute.name, attribute.value);
                }
            }

            // Create a fresh textarea with the correct name attribute
            const freshTextarea = document.createElement('textarea');
            freshTextarea.name = 'message_text';
            freshTextarea.className = 'rt-editor__textarea';
            freshTextarea.value = '';
            freshEditor.append(freshTextarea);

            // Replace the cloned editor with the fresh one
            clonedEditor.replaceWith(freshEditor);
        }

        // Show the cancel button for reply forms (it's hidden by default in the component)
        const cancelReplyButton = clonedForm.querySelector(this.constants.SELECTORS.cancelReplyButton);
        if (cancelReplyButton) {
            cancelReplyButton.classList.remove(this.constants.CSS_CLASSES.hidden);
        }

        // The reply composer is a clone of the main composer; adapt its (large, pulsing) Post button to the reply.
        this.normalizeReplySubmitButton(clonedForm);

        // Create temporary reply wrapper with connection lines for the cloned reply form
        const temporaryReplyWrapper = this.createTemporaryReplyWrapper(clonedForm, message);
        replyContainer.append(temporaryReplyWrapper);

        // Focus the editor and scroll to it after the cloned form is in the DOM
        // The rt-editor Web Component will automatically initialize when connected to the DOM
        const editor = clonedForm.querySelector('rt-editor');
        if (editor) {
            setTimeout(() => {
                // Clear any existing content and focus
                if (editor.value !== undefined) {
                    editor.value = '';
                }
                editor.focus();
                this.scrollViewportToElement(editor);
            }, 100);
        }

        // Add slide down animation
        replyContainer.classList.replace(this.constants.CSS_CLASSES.opacity0, this.constants.CSS_CLASSES.slideDown);
    }

    /**
     * Adapt a cloned reply form's Post button to the reply context: shrink it from large to small so it lines up
     * with the small Cancel button, and release the attention pulse so it runs its three pulses here too.
     * @param {HTMLElement} clonedForm - The cloned reply form
     */
    normalizeReplySubmitButton(clonedForm) {
        const submitButton = clonedForm.querySelector(this.constants.SELECTORS.replySubmitButton);
        if (!submitButton) {
            return;
        }

        // The source composer's Post button is large; match the small Cancel button beside it.
        submitButton.classList.remove('button--large', 'button--medium');
        submitButton.classList.add('button--small');

        // The pulse gate is only stripped by button.js's observer, which watches the original composer, never this
        // clone. Drop it here so the clone's bounded three-pulse runs right away instead of freezing.
        submitButton.parentElement?.classList.remove(this.constants.CSS_CLASSES.pulseInViewGate);
    }

    /**
     * Handle cancel reply button click - removes the reply form
     * @param {HTMLElement} cancelButton - The cancel button that was clicked
     */
    handleCancelReply(cancelButton) {
        const temporaryWrapper = cancelButton.closest('.js-temp-reply-wrapper');
        if (!temporaryWrapper) {
            return;
        }

        // Restore the vertical connection line on the last reply
        const repliesContainer = temporaryWrapper.closest(this.constants.SELECTORS.repliesContainer);
        const messageRoot = repliesContainer?.closest(this.constants.SELECTORS.messageRoot);
        this.updateLastReplyConnectionLine(messageRoot, false);

        temporaryWrapper.remove();
    }

    /**
     * Hide all active reply forms
     */
    hideAllReplyForms() {
        document.querySelectorAll('.js-reply-template').forEach(template => {
            template.hidden = true;
        });

        // Remove all temporary reply wrappers (they contain cloned forms)
        document.querySelectorAll('.js-temp-reply-wrapper').forEach(wrapper => {
            wrapper.remove();
        });

        document.querySelectorAll(this.constants.SELECTORS.repliesContainer).forEach(container => {
            container.classList.add(this.constants.CSS_CLASSES.opacity0);
        });
    }

    /**
     * Create a reply wrapper element with proper styling and connection lines
     * @param {HTMLElement} newMessage - The new message element
     * @returns {HTMLElement} The wrapper element
     */
    createReplyWrapper(newMessage) {
        const replyWrapper = document.createElement('div');
        replyWrapper.className = this.constants.REPLY_WRAPPER_CLASSES + ' relative';

        // Clone connection lines from template
        const connectionLinesTemplate = document.querySelector('.js-connection-lines-template');
        if (connectionLinesTemplate) {
            const connectionLinesContent = connectionLinesTemplate.content.cloneNode(true);
            replyWrapper.append(connectionLinesContent);
        }

        // Create a wrapper div with margin for the message content
        const messageWrapper = document.createElement('div');
        messageWrapper.className = 'mt-12';
        messageWrapper.append(newMessage);

        // Append the message wrapper
        replyWrapper.append(messageWrapper);

        return replyWrapper;
    }

    /**
     * Create a temporary reply wrapper for the reply form with connection lines
     * @param {HTMLElement} replyForm - The reply form element
     * @param {HTMLElement} parentMessage - The parent message element
     * @returns {HTMLElement} The temporary wrapper element
     */
    createTemporaryReplyWrapper(replyForm, parentMessage) {
        const temporaryWrapper = document.createElement('div');
        temporaryWrapper.className = this.constants.REPLY_WRAPPER_CLASSES + ' relative js-temp-reply-wrapper';

        // Clone connection lines from template
        const connectionLinesTemplate = document.querySelector('.js-connection-lines-template');
        if (connectionLinesTemplate) {
            const connectionLinesContent = connectionLinesTemplate.content.cloneNode(true);
            temporaryWrapper.append(connectionLinesContent);
        }

        // Extend the last reply's vertical connection line
        const parentMessageRoot = parentMessage?.closest(this.constants.SELECTORS.messageRoot);
        this.updateLastReplyConnectionLine(parentMessageRoot, true);

        temporaryWrapper.append(replyForm);
        return temporaryWrapper;
    }

    // ==========================================
    // EDIT MESSAGE METHODS
    // ==========================================

    /**
     * Handle edit message button click
     * @param {HTMLElement} editButton - The edit button that was clicked
     */
    handleEditMessage(editButton) {
        const messageBodyDetails = editButton.closest('[data-message-body-details]');
        messageBodyDetails.hidden = true;

        const message = this.getMessageByChild(editButton);
        const messageBody = message.querySelector(this.constants.SELECTORS.messageText);
        const editor = document.createElement('rt-editor');
        const uploadDestination = editButton.dataset.uploadDestination;

        if (uploadDestination) {
            editor.setAttribute('accept', editButton.dataset.accept);
            editor.setAttribute('upload-destination', uploadDestination);
        }

        const mentionAutocompleteUrl = editButton.dataset.mentionAutocompleteUrl;
        if (mentionAutocompleteUrl) {
            editor.setAttribute('mentions', 'on');
            editor.setAttribute('mentions-autocomplete-url', mentionAutocompleteUrl);
        }

        editor.setAttribute('emoji', 'on');

        editor.addEventListener('rte:connected', () => {
            messageBody.hidden = true;

            const editMessageButtonsContainer = document.querySelector(this.constants.SELECTORS.editTemplate);
            editor.insertAdjacentHTML('afterend', editMessageButtonsContainer.innerHTML);

            editor.value = messageBody.innerHTML;
            editor.focus();
        });

        messageBody.parentNode.insertBefore(editor, messageBody);
    }

    /**
     * Handle save message button click
     * @param {HTMLElement} saveButton - The save button that was clicked
     */
    async handleSaveMessage(saveButton) {
        const message = this.getMessageByChild(saveButton);
        if (!message) return;

        const messageId = message.dataset.messageId;
        const spinner = new Spinner(message);
        const messageBodyDetailsContainer = saveButton.closest('[data-discussion-message-body]');

        spinner.start();

        const editorElement = message.querySelector('rt-editor');
        const data = {
            message_text: editorElement && editorElement.value !== undefined ? editorElement.value : '',
        };

        try {
            const response = await this.httpClient.put(`/conversation/messages/${messageId}`, data);

            if (response) {
                this.updateEditedMessage(message, response.message_text);
                if (messageBodyDetailsContainer) {
                    this.exitEditMode(message, messageBodyDetailsContainer);
                }
            }
        } catch (error) {
            this.handleEditError(error, message);
        } finally {
            spinner.stop();
            window.scrollY -= 1; // Hack to re-locate RTE
        }
    }

    /**
     * Update message content after successful edit
     * @param {HTMLElement} message - The message element
     * @param {string} newContent - The new message content
     */
    updateEditedMessage(message, newContent) {
        const editableElement = message.querySelector(this.constants.SELECTORS.messageText);
        editableElement.hidden = false;
        editableElement.innerHTML = newContent;
        this.removeEditFormElements(message);
    }

    /**
     * Exit edit mode and show message details
     * @param {HTMLElement} message - The message element
     * @param {HTMLElement} messageBodyDetailsContainer - The message body container
     */
    exitEditMode(message, messageBodyDetailsContainer) {
        const messageBodyDetails = messageBodyDetailsContainer.querySelector('[data-message-body-details]');
        messageBodyDetails.hidden = false;
    }

    /**
     * Handle edit operation errors
     * @param {Error} error - The error object
     * @param {HTMLElement} message - The message element
     */
    handleEditError(error, message) {
        let errorMessage = error.message;

        if (error.status === 422 && error.errorBody?.errors?.message_text) {
            errorMessage = error.errorBody.errors.message_text.join('\n');
        }

        const editControls = message.querySelector(this.constants.SELECTORS.editControls);
        Notification.error(errorMessage, editControls);
    }

    /**
     * Handle cancel edit button click
     * @param {HTMLElement} cancelButton - The cancel button that was clicked
     */
    handleCancelEdit(cancelButton) {
        const message = this.getMessageByChild(cancelButton);
        const editableElement = message.querySelector(this.constants.SELECTORS.messageText);
        const messageBodyDetailsContainer = cancelButton.closest('[data-discussion-message-body]');

        editableElement.hidden = false;
        this.exitEditMode(message, messageBodyDetailsContainer);
        this.removeEditFormElements(message);
    }

    /**
     * Remove edit form elements from message
     * @param {HTMLElement} message - The message element
     */
    removeEditFormElements(message) {
        message.querySelector('rt-editor')?.remove();
        message.querySelector(this.constants.SELECTORS.editControls)?.remove();
    }

    // ==========================================
    // DELETE MESSAGE METHODS
    // ==========================================

    /**
     * Handle delete message operation
     * @param {HTMLElement} originalMessage - The message to delete
     */
    async handleDeleteMessage(originalMessage) {
        const messageId = originalMessage.dataset.messageId;
        const spinner = new Spinner(originalMessage);

        // Check if this is the TOPIC message (the original discussion starter)
        // Topic message: data-is-topic-message="true" - should redirect to /conversations
        // Comment: data-is-topic-message="false" and data-is-reply="false" - should NOT redirect
        // Nested reply: data-is-reply="true" - should NOT redirect
        const isTopicMessage = originalMessage.dataset.isTopicMessage === 'true';

        spinner.start(t('Deleting message…'), t('Please wait…'));

        try {
            const response = await this.httpClient.delete(`/conversation/messages/${messageId}`);
            if (response) {
                // If it's the topic message (original discussion starter), redirect to conversations page
                // This applies regardless of who deletes it (admin or owner)
                if (isTopicMessage) {
                    window.location.href = '/conversations';
                    return;
                }

                // If it's a comment or nested reply, just remove it from DOM
                this.removeMessageFromDOM(originalMessage);
            }
        } catch (error) {
            Notification.error(error.message, originalMessage);
        } finally {
            // Only stop spinner if we're not redirecting
            if (!isTopicMessage) {
                spinner.stop();
            }
        }
    }

    /**
     * Remove message from DOM based on its type (reply or main message)
     * @param {HTMLElement} originalMessage - The message to remove
     */
    removeMessageFromDOM(originalMessage) {
        // Check if this is a reply message
        const replyWrapper = originalMessage.closest(this.constants.SELECTORS.replyMessageWrapper);

        if (replyWrapper) {
            // This is a reply - just remove the reply wrapper
            replyWrapper.remove();
            return;
        }

        // This is a main message - remove the entire message root including all replies
        const messageRoot = originalMessage.closest(this.constants.SELECTORS.messageRoot);
        if (messageRoot) {
            // Remove the entire message thread (parent + all replies)
            messageRoot.remove();
            return;
        }

        // Fallback: remove just the message if no root found
        const listItem = originalMessage.closest('li');
        if (listItem) {
            listItem.remove();
        } else {
            originalMessage.remove();
        }
    }

    // ==========================================
    // UTILITY METHODS
    // ==========================================

    /**
     * Update the vertical connection line height on the last reply
     * @param {HTMLElement|null} messageRoot - The message root element
     * @param {boolean} extend - Whether to extend (h-full) or restore (h-40) the line
     */
    updateLastReplyConnectionLine(messageRoot, extend) {
        if (!messageRoot) return;

        const messageRepliesWrapper = messageRoot.querySelector(this.constants.SELECTORS.messageRepliesWrapper);
        const existingReplies = messageRepliesWrapper?.querySelectorAll(this.constants.SELECTORS.replyMessageWrapper);

        if (existingReplies && existingReplies.length > 0) {
            // eslint-disable-next-line unicorn/prefer-at -- NodeList.at() compatibility issue
            const lastReply = existingReplies[existingReplies.length - 1];
            const verticalLine = lastReply.querySelector('.js-message-vertical-connection-line');

            if (verticalLine) {
                if (extend) {
                    verticalLine.classList.remove('h-40');
                    verticalLine.classList.add('h-full');
                } else {
                    verticalLine.classList.remove('h-full');
                    verticalLine.classList.add('h-40');
                }
            }
        }
    }

    /**
     * Get root message element from any child element
     * @param {HTMLElement} child - Child element within message
     * @returns {HTMLElement|null} Root message element
     */
    getRootMessageByChild(child) {
        return child.closest(this.constants.SELECTORS.messageRoot);
    }

    /**
     * Get message element from any child element
     * @param {HTMLElement} child - Child element within message
     * @returns {HTMLElement|null} Message element
     */
    getMessageByChild(child) {
        return child.closest(this.constants.SELECTORS.message);
    }

    /**
     * Scroll viewport to element if not fully visible
     * @param {HTMLElement} element - Element to scroll to
     */
    scrollViewportToElement(element) {
        const rect = element.getBoundingClientRect();
        const isInViewport = (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= getWindowHeight() &&
            rect.right <= getWindowWidth()
        );

        // Only scroll if the element is not fully visible
        if (!isInViewport) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }
}

// ==========================================
// INITIALIZATION
// ==========================================

/**
 * Initialize the discussion manager when DOM is ready
 */
function initializeDiscussionManager() {
    new DiscussionManager();
}

// Initialize when document is ready
document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', initializeDiscussionManager)
    : initializeDiscussionManager();
